import { FC, useEffect, useState } from "react";

import { MenuItem, TextField } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  GeoDataFileProcessingRunModel,
  listGeoDataFileProcessingRun,
  listSubregion,
  SubregionModel,
} from "@rubi/api-generated";
import { useNavigate, useParams } from "react-router-dom";

import { DevPage, ErrorMsg, Section } from "./common";
import {
  onRowActivateKeyDown,
  RegionBreadcrumbs,
  RegionSubNav,
} from "./regionShared";

const ALL_RUNS = "";

const columns: GridColDef<SubregionModel>[] = [
  { field: "id", headerName: "ID", flex: 1, minWidth: 280 },
  {
    field: "geo_data_file_processing_run_id",
    headerName: "Processing Run",
    flex: 1,
    minWidth: 280,
  },
  {
    field: "metadata",
    headerName: "Metadata",
    flex: 2,
    minWidth: 300,
    valueGetter: (value: SubregionModel["metadata"]) => JSON.stringify(value),
  },
];

export const DevSubregionListPage: FC = () => {
  const { orgId = "" } = useParams();
  const navigate = useNavigate();

  const [runs, setRuns] = useState<GeoDataFileProcessingRunModel[]>([]);
  const [selectedRunId, setSelectedRunId] = useState(ALL_RUNS);
  const [subregions, setSubregions] = useState<SubregionModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listGeoDataFileProcessingRun({ path: { org_id: orgId } }).then((response) =>
      setRuns(response.data?.geo_data_file_processing_runs ?? []),
    );
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSubregions([]);
    listSubregion({
      path: { org_id: orgId },
      query: {
        geo_data_file_processing_run_id: selectedRunId || undefined,
      },
      throwOnError: false,
    }).then((response) => {
      if (cancelled) return;
      if (response.status === 200) {
        setSubregions(response.data?.subregions ?? []);
        setError(null);
      } else {
        setError(`${response.status} ${response.error?.console_msg ?? ""}`);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [orgId, selectedRunId]);

  const openSubregion = (subregionId: string) =>
    navigate(`/dev_admin/region/${orgId}/subregion/${subregionId}`);

  return (
    <DevPage
      title="Subregions"
      breadcrumbs={<RegionBreadcrumbs orgId={orgId} />}
    >
      <RegionSubNav orgId={orgId} active="subregion" />

      <Section subtitle="Filter">
        <TextField
          select
          size="small"
          label="Processing run"
          value={selectedRunId}
          onChange={(e) => setSelectedRunId(e.target.value)}
          sx={{ minWidth: 320 }}
        >
          <MenuItem value={ALL_RUNS}>All runs</MenuItem>
          {runs.map((run) => (
            <MenuItem key={run.id} value={run.id}>
              {run.id} — {run.status}
            </MenuItem>
          ))}
        </TextField>
      </Section>

      <Section subtitle="Subregions">
        <ErrorMsg msg={error} />
        <div style={{ width: "100%" }}>
          <DataGrid
            rows={subregions}
            columns={columns}
            loading={loading}
            autoHeight
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            onRowClick={(params) => openSubregion(String(params.id))}
            onCellKeyDown={onRowActivateKeyDown(openSubregion)}
            sx={{ cursor: "pointer" }}
          />
        </div>
      </Section>
    </DevPage>
  );
};
